import { useState, useEffect } from "react";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import { DateTime } from 'luxon';
import TestEditModal from "@modals/TestEditModal"; // Adjust the import path as needed
import { GoGear } from "react-icons/go";
import Helpers from "../Helpers";
import { toast } from "react-toastify";
import { LuChevronsDown, LuChevronsUp } from "react-icons/lu";
import ClientSortableTable from "@elements/ClientSortableTable";
import TestDetailsModal from "@modals/TestDetailsModal"; // Adjust the import path as needed
import TestInstanceModal from "@modals/TestInstanceModal";
import { FaFileDownload } from "react-icons/fa";
import { MdMonitorHeart, MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import JobDescriptionModal from "@modals/JobDescriptionModal";
import { useDashboardSignalR } from "@/utils/useDashboardSignalR";
import TestMonitorModal from "@modals/TestMonitorModal";
import AuditLogModal from "@modals/AuditLogModal";
import { billableRowClass, testTableRowClass } from "@/utils/testRowHelpers";
import { downloadAuthenticatedFile } from "@/utils/authenticatedFileDownload";
import { formatElapsedDdHhMm } from "@/utils/formatElapsedDdHhMm";
import { useIntervalTick } from "@/utils/useIntervalTick";

type DashboardErrorRow = {
  id?: number;
  when?: string;
  severity?: number;
  message?: string;
  context?: string;
  companyId?: number;
  companyName?: string;
};

export default function AdminDashboardTab(props: any) {
  const { updateSpinner, activeTab, adminPerms } = props.tabData;
  const [isEditModalOpen, setIsModalOpen] = useState(false);
  const [selectedTests, updateSelectedTests] = useState([]);
  const [testInstanceId, setTestInstanceId] = useState<number | null>(null);
  const [detailsActiveTab, setDetailsActiveTab] = useState("details");
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [jobDescriptionJobId, setJobDescriptionJobId] = useState<number | null>(null);
  const [dragOverJobIndex, setDragOverJobIndex] = useState<number | null>(null); // highlight drop target
  const [signalRMessages, setSignalRMessages] = useState<{
    eventType?: string;
    companyName?: string;
    companyId?: number;
    testId?: number;
    receivedAt?: string;
    firstName?: string;
    lastName?: string;
    userName?: string;
  }[]>([]);
  const [monitorTestInstanceId, setMonitorTestInstanceId] = useState<number | null>(null);
  const [instanceModalState, setInstanceModalState] = useState<{ testId: number | null; testInstanceId: number | null }>({ testId: null, testInstanceId: null });

  const [selectedAuditLogId, setSelectedAuditLogId] = useState<number | null>(null);
  const [selectedStage, setSelectedStage] = useState<string>("pending");
  const isDashboardTab = activeTab === "dashboard";
  const hasActiveTests = Array.isArray(dashboardData?.activeTests) && dashboardData.activeTests.length > 0;
  useIntervalTick(5000, isDashboardTab && hasActiveTests);

  useEffect(() => {
    if (isDashboardTab) {
      getDashboardDataAsync();
    }
  }, [activeTab]);

  function formatApplicantLabel(test) {
    const fullName = [String(test?.firstName || "").trim(), String(test?.lastName || "").trim()]
      .filter(Boolean)
      .join(" ");

    return fullName || String(test?.userName || "").trim();
  }

  function findApplicantForToast(eventData, snapshot) {
    const testCollections = [
      snapshot?.recentTests,
      snapshot?.gradableTests,
      snapshot?.pendingTests,
      snapshot?.activeTests,
    ];

    for (const tests of testCollections) {
      if (!Array.isArray(tests)) continue;

      const match = tests.find((test) => {
        const matchesTestId = eventData?.testId && Number(test?.testId || 0) === Number(eventData.testId);
        const matchesInstanceId = eventData?.testInstanceId && Number(test?.testInstanceId || 0) === Number(eventData.testInstanceId);
        return matchesTestId || matchesInstanceId;
      });

      const applicantLabel = formatApplicantLabel(match);
      if (applicantLabel) {
        return {
          label: applicantLabel,
          firstName: String(match?.firstName || "").trim(),
          lastName: String(match?.lastName || "").trim(),
          userName: String(match?.userName || "").trim(),
        };
      }
    }

    return null;
  }

  function severityToLabel(severity: number | undefined) {
    switch (Number(severity || 0)) {
      case 40:
        return "Error";
      case 50:
        return "Exception";
      case 60:
        return "Critical";
      case 30:
        return "Warning";
      case 20:
        return "Debug";
      case 10:
        return "Info";
      default:
        return String(severity ?? "Unknown");
    }
  }

  useDashboardSignalR(async (data) => {
    if (!isOnThisTab()) return;
    if (data?.eventType === "auditError") {
      const errorRow: DashboardErrorRow = {
        id: data?.id,
        when: data?.when || data?.receivedAt,
        severity: data?.severity,
        message: data?.message,
        context: data?.context,
        companyId: data?.companyId,
        companyName: data?.companyName,
      };

      setDashboardData((prev) => {
        if (!prev) return prev;
        const existing = Array.isArray(prev.recentErrors) ? prev.recentErrors : [];
        const deduped = existing.filter((e: DashboardErrorRow) => Number(e?.id || 0) !== Number(errorRow?.id || 0));
        return {
          ...prev,
          recentErrors: [errorRow, ...deduped].slice(0, 100),
        };
      });

      const severityLabel = severityToLabel(Number(data?.severity || 0));
      const companyLabel = data?.companyName ?? (data?.companyId ? `Company ${data.companyId}` : "System");
      toast.error(`${severityLabel}: ${data?.message || "Audit error"} (${companyLabel})`);
    } else {
      const refreshedData = await getDashboardDataAsync();
      const applicant = findApplicantForToast(data, refreshedData) || findApplicantForToast(data, dashboardData);
      const payloadName = [data?.firstName, data?.lastName].filter(Boolean).join(" ").trim();
      const applicantLabel = applicant?.label || payloadName;
      const label = data?.companyName ?? (data?.companyId ? `Company ${data.companyId}` : "Unknown");
      const subjectLabel = applicantLabel ? `${applicantLabel} at ${label}` : label;
      const testLabel = data?.testId ? ` (Test #${data.testId})` : "";
      if (data?.eventType === "testStarted") toast.info(`Test started for ${subjectLabel}${testLabel}`);
      else if (data?.eventType === "testCreated") toast.info(`New test created for ${subjectLabel}${testLabel}`);
      else if (data?.eventType === "testCompleted") toast.info(`Test completed for ${subjectLabel}${testLabel}`);
      else if (data?.eventType === "testGraded") toast.success(`Test graded for ${subjectLabel}${testLabel}`);

      setSignalRMessages(prev => {
        const cutoff = Date.now() - 24 * 60 * 60 * 1000;
        const enrichedMessage = {
          ...data,
          firstName: data?.firstName || applicant?.firstName,
          lastName: data?.lastName || applicant?.lastName,
          userName: data?.userName || applicant?.userName,
        };
        return [enrichedMessage, ...prev.filter(m =>
          !(m.companyId === data?.companyId && m.testId === data?.testId) &&
          (m.receivedAt ? new Date(m.receivedAt).getTime() >= cutoff : true)
        )].slice(0, 20);
      });
      return;
    }

    setSignalRMessages(prev => {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      return [{ ...data }, ...prev.filter(m =>
        !(m.companyId === data?.companyId && m.testId === data?.testId) &&
        (m.receivedAt ? new Date(m.receivedAt).getTime() >= cutoff : true)
      )].slice(0, 20);
    });
  }, { enabled: isDashboardTab });

  // Don't render until we're on the dashboard tab AND data is loaded
  if (!isDashboardTab || !dashboardData) return null;

  function isOnThisTab() {
    return isDashboardTab;
  }

  async function getDashboardDataAsync() {
    updateSpinner(true);
    const resp = await RightTimeApi.getAsync("/AdmDashboard");
    updateSpinner(false);
    if (resp && resp.ok && resp.jsonData) {
      setDashboardData({
        ...resp.jsonData,
        adminTasks: resp.jsonData.adminTasks ?? [],
      });
      return resp.jsonData;
    }

    return null;
  }

  async function onCloseTestEditModalAsync() {
    setIsModalOpen(false);
    updateSelectedTests([]);
    await getDashboardDataAsync();
  }

  function onToggleRow(e, testId) {
    e.preventDefault();
    if (selectedTests.includes(testId)) {
      // Remove the testId if it already exists in the array
      updateSelectedTests(selectedTests.filter((id) => id !== testId));
    } else {
      // Add the testId if it doesn't exist in the array
      updateSelectedTests([...selectedTests, testId]);
    }
  }

  async function onSetStatusAsync(e) {
    const id = parseInt(e.target.getAttribute("data-testId"));
    const testInstanceId = parseInt(e.target.getAttribute("data-testinstanceid"));
    const data = {
      testId: id,
      testInstanceId: testInstanceId,
      newStatusId: parseInt(e.target.value),
    };
    updateSpinner(true);
    const resp = await RightTimeApi.putAsync("/TestStatus", data);
    updateSpinner(false);
    if (resp && resp.ok) {
      toast.success("Test status updated.");
      await getDashboardDataAsync();
    }
  }

  async function onToggleCollapse(e) {
    const tableWrapper = e.target.closest(".tableWrapper"); // Find the closest parent with the class "tableWrapper"
    if (tableWrapper) {
      tableWrapper.classList.toggle("collapsed"); // Toggle the "collapsed" class
    }
  }

  async function showScoreModal(e) {
    // Find the closest td element to handle clicks on child elements

    const cell = e.target.closest('td');
    if (cell) {
      const testId = cell.getAttribute("data-testid");
      if (testId) {
        setTestInstanceId(parseInt(testId));
        setDetailsActiveTab("scores");
      }
    }
  }

  async function closeScoreModal() {
    setTestInstanceId(null);
    setDetailsActiveTab("details");
    await getDashboardDataAsync();
  }

  function openMonitorModal(e, testInstanceId) {
    e.stopPropagation();
    setMonitorTestInstanceId(testInstanceId);
  }

  const downloadFile = async (item, event) => {
    // Prevent row click when clicking the download icon
    event.stopPropagation();

    if (!item) {
      toast.error("No file ID available for download.");
      return;
    }

    try {
      const jdId = item.description?.companyJobDescriptionId ?? item.description?.id;
      if (!item.id || !jdId) {
        toast.error("No file ID available for download.");
        return;
      }
      const ok = await downloadAuthenticatedFile(
        `/CompanyJob/${item.id}/Description/${jdId}/File`,
        item.description?.attachmentFileName || "download"
      );
      if (ok) toast.success("File downloaded successfully!");
      else toast.error("Error downloading file.");
    } catch (error) {
      toast.error("Error downloading file.");
      console.error('Error downloading file:', error);
    }
  }

  async function handleJobFileDrop(item: any, event: any) {
    event.preventDefault();
    setDragOverJobIndex(null);
    // Preserve current behavior; upload workflow is not implemented in this component yet.
    toast.info(`Drop received for job ${item?.id}. Upload flow is not enabled from this view.`);
  }

  async function editTestInstanceAsync(e) {
    let testInstanceId = parseInt(e.currentTarget.getAttribute("data-testinstanceid"));
    let testId = parseInt(e.currentTarget.getAttribute("data-testid"));
    console.log("Editing test instance:", testInstanceId);
    if (e.currentTarget.getAttribute("data-help-id") === "testPendingGrading") {
      setDetailsActiveTab("instances");
    }
    setTestInstanceId(testInstanceId);
  }

  function renderRecentTests() {
    return (
      <ClientSortableTable
        columns={[
          { key: "testId", label: "TestId", filterable: false, sortable: true },
          { key: "companyName", label: "Company", filterable: true, sortable: true },
          { key: "siteName", label: "Site", filterable: true, sortable: true },
          { key: "testType", label: "Test Type", filterable: true, sortable: true },
          { key: "status", label: "Status", filterable: true, sortable: true },
          { key: "created", label: "Created", filterable: false, sortable: false },
          { key: "completed", label: "Completed", filterable: false, sortable: false },
          { key: "userName", label: "User", filterable: true, sortable: true },
          { key: "languageAbbrev", label: "Lang", filterable: false, sortable: false },
          { key: "scores", label: "Scores", filterable: false, sortable: false },
        ]}
        data={dashboardData.recentTests}
        tableClass="clientSortable clientFilterable expanded"
        caption={
          <>
            <span onClick={onToggleCollapse} className="tableCaption">
              Assessments Recently Completed
              <span className="captionCount">({dashboardData.recentTests?.length.toString()})</span>
            </span>
            <span className="captionRight">
              {dashboardData.recentTests ? (
                <>
                  <LuChevronsDown className="collapse" />
                  <LuChevronsUp className="expand" />
                </>
              ) : null}
            </span>
          </>
        }
        onCaptionClick={onToggleCollapse}
        renderRow={(item, index) => {
          return (
            <tr key={index} data-testinstanceid={item.testInstanceId} onClick={editTestInstanceAsync} className={billableRowClass(item.billable)}>
              <td>{item.testId}</td>
              <td>{item.companyName}</td>
              <td>{item.siteName}</td>
              <td>{item.testTypeName || item.testType}</td>
              <td>{item.companyTestStatusDisplay} </td>
              <td>{Helpers.EST(item.created || item.createTime, "MM/dd/yyyy")}</td>
              <td>{Helpers.EST(item.completed || item.submitTime, "MM/dd/yyyy")}</td>
              <td>
                {item.userName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : "")}
              </td>
              <td>{item.languageAbbrev}</td>
              <td data-testid={item.testId} onClick={showScoreModal}
                className={`{item.scores && item.scores.some(score => score.message) ? 'has-tooltip' : ''}`}>
                {item.scores && item.scores.length > 0
                  ? item.scores.filter(s => s.score && s.score.length > 0).map((score, idx) => {
                    let typeName = "";
                    switch (score.scoreType) {
                      case 1:
                        typeName = "Company";
                        break;
                      case 2:
                        typeName = "Applicant";
                        break;
                      case 3:
                        typeName = "Overall";
                        break;
                      default:
                        typeName = "Unknown";
                    }
                    return (
                      <div key={score.id || idx}>
                        {typeName}: {score.score}
                      </div>
                    );
                  })
                  : null}
              </td>
            </tr>
          );
        }}
      />);
  }

  function renderJobs() {
    return (
      <ClientSortableTable
        columns={[
          { key: "id", label: "Id", filterable: false, sortable: false },
          { key: "name", label: "Job", filterable: false, sortable: false },
          { key: "description", label: "Description", filterable: false, sortable: true },
          { key: "state", label: "Status", filterable: true, sortable: true },
          { key: "File", label: "Attachment", filterable: false, sortable: false },
        ]}
        data={dashboardData.jobActions}
        tableClass="clientSortable clientFilterable collapsed"
        caption={
          <>
            <span onClick={onToggleCollapse} className="tableCaption">
              Job Descriptions
              <span className="captionCount">({dashboardData.jobActions?.length.toString()})</span>
            </span>
            {dashboardData.jobActions ? (
              <span className="captionRight">
                <LuChevronsDown className="collapse" />
                <LuChevronsUp className="expand" />
              </span>
            ) : null}
          </>
        }
        onCaptionClick={onToggleCollapse}
        renderRow={(item, index) => {
          return (
            <tr key={index} >
              <td onClick={() => setJobDescriptionJobId(item.id)} className="underline">{item.id}</td>
              <td onClick={() => setJobDescriptionJobId(item.id)} className="underline">{item.name}</td>
              <td>{item.description.description}</td>
              <td>{item.description.state}</td>
              <td
                className={`drop-target ${dragOverJobIndex === index ? 'drag-over' : ''}`}
                onDragOver={(e) => {
                  // Allow dropping
                  e.preventDefault();
                }}
                onDragEnter={() => setDragOverJobIndex(index)}
                onDragLeave={() => setDragOverJobIndex(null)}
                onDrop={(e) => handleJobFileDrop(item, e)}
                title="Drop a file to upload"
                onClick={(e) => downloadFile(item, e)}
              >
                {item.description.blobFileId && (
                  <>
                    <FaFileDownload title="Download attachment" className="actionIcon" />
                    {item.description.blobFileName}
                  </>
                )}
              </td>
            </tr>
          );
        }}
      />
    );
  }

  function renderGradableTests() {
    return (
      <ClientSortableTable
        columns={[
          { key: "testId", label: "TestId", filterable: false, sortable: true },
          { key: "companyName", label: "Company", filterable: true, sortable: true },
          { key: "siteName", label: "Site", filterable: true, sortable: true },
          { key: "testType", label: "Test Type", filterable: true, sortable: true },
          { key: "created", label: "Created", filterable: false, sortable: false },
          { key: "completed", label: "Completed", filterable: false, sortable: false },
          { key: "userName", label: "User", filterable: true, sortable: true },
          { key: "languageAbbrev", label: "Lang", filterable: false, sortable: false },
        ]}
        data={dashboardData.gradableTests}
        tableClass="clientSortable clientFilterable expanded gradableTestsTable"
        caption={
          <>
            <span onClick={onToggleCollapse} className="tableCaption">
              Assessments Pending Grading
              <span className="captionCount">({dashboardData.gradableTests.length.toString()})</span>
            </span>
            <span className="captionRight">
              {dashboardData.gradableTests ? (
                <>
                  <span className="captionRight"><LuChevronsDown className="collapse" /><LuChevronsUp className="expand" /></span>
                </>
              ) : null}
            </span>
          </>
        }
        onCaptionClick={onToggleCollapse}
        renderRow={(item, index) => {
          return (
            <tr key={index} data-testid={item.testId} data-testinstanceid={item.testInstanceId} onClick={editTestInstanceAsync} data-help-id="testPendingGrading" className={billableRowClass(item.billable)}>
              <td>{item.testId}</td>
              <td>{item.companyName}</td>
              <td>{item.siteName}</td>
              <td>{item.testTypeName || item.testType}</td>

              <td>{Helpers.EST(item.created || item.createTime, "MM/dd/yyyy")}</td>
              <td>{Helpers.EST(item.completed || item.submitTime, "MM/dd/yyyy")}</td>
              <td>
                {item.userName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : '')}
              </td>
              <td>{item.languageAbbrev}</td>
              
            </tr>
          );
        }}
      />);
  }

  function renderPendingTests() {
    return (
      <ClientSortableTable
        columns={[
          { key: "testId", label: "TestId", filterable: false, sortable: true },
          { key: "companyName", label: "Company", filterable: true, sortable: true },
          { key: "siteName", label: "Site", filterable: true, sortable: true },
          { key: "testType", label: "Test Type", filterable: true, sortable: true },
          { key: "created", label: "Created", filterable: false, sortable: false },
          { key: "userName", label: "User", filterable: true, sortable: true },
          { key: "codeExpiration", label: "Code Expiration", filterable: false, sortable: false },
          { key: "monitor", label: "Monitor", filterable: false, sortable: false },
        ]}
        data={dashboardData.pendingTests}
        tableClass="clientSortable clientFilterable expanded pendingTestsTable"
        caption={
          <>
            <span onClick={onToggleCollapse} className="tableCaption">
              Assessments Pending User
              <span className="captionCount">({dashboardData.pendingTests.length.toString()})</span>
            </span>
            <span className="captionRight">
              {dashboardData.pendingTests ? (
                <>
                  <span className="captionRight"><LuChevronsDown className="collapse" /><LuChevronsUp className="expand" /></span>
                </>
              ) : null}
            </span>
          </>
        }
        onCaptionClick={onToggleCollapse}
        renderRow={(item, index) => {
          const expiryClass = item.codeExpiration && new Date(item.codeExpiration) < new Date()
            ? "expired"
            : item.codeExpiration && DateTime.fromISO(item.codeExpiration).diffNow("hours") > -24 && DateTime.fromISO(item.codeExpiration).diffNow("hours") < 0
              ? "expiring"
              : "";
          return (
            <tr key={index} data-testinstanceid={item.testInstanceId} onClick={editTestInstanceAsync}
              className={testTableRowClass(billableRowClass(item.billable), expiryClass)}
              data-help-id={item.codeExpiration && new Date(item.codeExpiration) < new Date() ? "expired-code" : item.codeExpiration && DateTime.fromISO(item.codeExpiration).diffNow("hours") > -24 && DateTime.fromISO(item.codeExpiration).diffNow("hours") < 0 ? "expiring-code" : ""}
              >
              <td>{item.testId}</td>
              <td>{item.companyName}</td>
              <td>{item.siteName}</td>
              <td>{item.testTypeName || item.testType}</td>
              <td title={Helpers.EST(item.created || item.createTime, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.created || item.createTime, "MM/dd/yyyy")}</td>
              <td>
                {item.userName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : '')}
              </td>
              <td title={Helpers.EST(item.codeExpiration, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.codeExpiration, "MM/dd/yyyy")}</td>
              <td onClick={(e) => openMonitorModal(e, item.testInstanceId)} data-help-id="monitorTest">
                <MdMonitorHeart className="actionIcon" title="Open monitor"/>
              </td>
            </tr>
          );
        }}
      />);
  }

  function renderTimedOutTests() {
    return (
      <ClientSortableTable
        columns={[
          { key: "testId", label: "TestId", filterable: false, sortable: true },
          { key: "companyName", label: "Company", filterable: true, sortable: true },
          { key: "siteName", label: "Site", filterable: true, sortable: true },
          { key: "testType", label: "Test Type", filterable: true, sortable: true },
          { key: "created", label: "Created", filterable: false, sortable: false },
          { key: "codeExpiration", label: "Code Expiration", filterable: false, sortable: false },
          { key: "userName", label: "User", filterable: true, sortable: true },
        ]}
        data={dashboardData.timedOutTests}
        tableClass="clientSortable clientFilterable expanded timedOutTestsTable"
        caption={
          <>
            <span onClick={onToggleCollapse} className="tableCaption">
              Timed Out / Expired Tests
              <span className="captionCount">({dashboardData.timedOutTests.length.toString()})</span>
            </span>
            <span className="captionRight">
              {dashboardData.timedOutTests ? (
                <>
                  <span className="captionRight"><LuChevronsDown className="collapse" /><LuChevronsUp className="expand" /></span>
                </>
              ) : null}
            </span>
          </>
        }
        onCaptionClick={onToggleCollapse}
        renderRow={(item, index) => {
          return (
            <tr key={index} data-testinstanceid={item.testInstanceId} onClick={editTestInstanceAsync} className={billableRowClass(item.billable)}>
              <td>{item.testId}</td>
              <td>{item.companyName}</td>
              <td>{item.siteName}</td>
              <td>{item.testTypeName || item.testType}</td>
              <td title={Helpers.EST(item.created || item.createTime, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.created || item.createTime, "MM/dd/yyyy")}</td>
              <td title={Helpers.EST(item.codeExpiration, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.codeExpiration, "MM/dd/yyyy")}</td>
              <td>
                {item.userName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : '')}
              </td>
            </tr>
          );
        }}
      />);
  }


  function renderActiveTests() {
    return (
      <ClientSortableTable
        columns={[
          { key: "testId", label: "TestId", filterable: false, sortable: true },
          { key: "companyName", label: "Company", filterable: true, sortable: true },
          { key: "siteName", label: "Site", filterable: true, sortable: true },
          { key: "testType", label: "Test Type", filterable: true, sortable: true },
          { key: "created", label: "Created", filterable: false, sortable: false },
          { key: "startTime", label: "Start Time", filterable: false, sortable: true },
          { key: "duration", label: "Duration", subLabel: "(dd:hh:mm)", filterable: false, sortable: false },
          { key: "userName", label: "User", filterable: true, sortable: true },
          { key: "monitor", label: "Monitor", filterable: false, sortable: false },
        ]}
        data={dashboardData.activeTests}
        tableClass="clientSortable clientFilterable expanded"
        caption={
          <>
            <span onClick={onToggleCollapse} className="tableCaption">
              Assessments In Progress
              <span className="captionCount">({dashboardData.activeTests.length.toString()})</span>
            </span>
            <span className="captionRight">
              {dashboardData.activeTests ? (
                <>
                  <span className="captionRight"><LuChevronsDown className="collapse" /><LuChevronsUp className="expand" /></span>
                </>
              ) : null}
            </span>
          </>
        }
        onCaptionClick={onToggleCollapse}
        renderRow={(item, index) => {
          const duration = formatElapsedDdHhMm(item.startTime, Date.now());
          return (
            <tr key={index} data-testinstanceid={item.testInstanceId} onClick={editTestInstanceAsync} className={billableRowClass(item.billable)}>
              <td>{item.testId}</td>
              <td>{item.companyName}</td>
              <td>{item.siteName}</td>
              <td>{item.testTypeName || item.testType}</td>

              <td title={Helpers.EST(item.created || item.createTime, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.created || item.createTime, "MM/dd/yyyy")}</td>
              <td title={Helpers.EST(item.startTime, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.startTime, "MM/dd/yyyy")}</td>
              <td title={duration}>{duration}</td>
              <td>
                {item.userName || (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : '')}
              </td>
              <td onClick={(e) => openMonitorModal(e, item.testInstanceId)}>
                <MdMonitorHeart className="actionIcon" title="Open monitor" />
              </td>
            </tr>
          );
        }}
      />);
  }

  function renderSignalRMessages() {
    if (signalRMessages.length === 0) return null;
    const eventLabels = { testStarted: "Test Started", testCreated: "Test Created", testCompleted: "Test Completed", testGraded: "Test Graded", auditError: "Audit Error" };
    return (
      <div className="tableWrapper dimensionTable scrollable collapsable">
        <table className="">
          <caption onClick={onToggleCollapse}>
            <span className="title">
              Live Events
              <span className="captionCount">({signalRMessages.length})</span>
            </span>
            <span className="captionRight">
              <LuChevronsDown className="collapse" />
              <LuChevronsUp className="expand" />
            </span>
          </caption>
          <thead>
            <tr>
              <th>Time</th>
              <th>Event</th>
              <th>Name</th>
              <th>Company</th>
              <th>Test ID</th>
            </tr>
          </thead>
          <tbody>
            {signalRMessages.map((msg, i) => (
              <tr key={i}>
                <td title={Helpers.EST(msg.receivedAt, "MM/dd/yyyy h:mm:ss a")}>
                  {Helpers.EST(msg.receivedAt, "h:mm:ss a")}
                </td>
                <td>{eventLabels[msg.eventType] ?? msg.eventType}</td>
                <td>{[msg.firstName, msg.lastName].filter(Boolean).join(" ") || msg.userName || "—"}</td>
                <td>{msg.companyName ?? (msg.companyId ? `Company ${msg.companyId}` : "—")}</td>
                <td>{msg.testId ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderRecentErrors() {
    const recentErrors = Array.isArray(dashboardData?.recentErrors) ? dashboardData.recentErrors : [];
    return (
      <ClientSortableTable
        columns={[
          { key: "when", label: "When", filterable: false, sortable: true },
          { key: "message", label: "Message", filterable: true, sortable: false },
        ]}
        data={recentErrors}
        tableClass="clientSortable clientFilterable collapsable dashboardErrorsTable"
        caption={
          <>
            <span onClick={onToggleCollapse} className="tableCaption">
              Recent System Errors (Last 24 Hours)
              <span className="captionCount">({recentErrors.length.toString()})</span>
            </span>
            <span className="captionRight">
              <LuChevronsDown className="collapse" />
              <LuChevronsUp className="expand" />
            </span>
          </>
        }
        onCaptionClick={onToggleCollapse}
        renderRow={(item: DashboardErrorRow, index) => (
          <tr
            key={item?.id || index}
            onClick={() => {
              const id = Number(item?.id || 0);
              if (id > 0) setSelectedAuditLogId(id);
            }}
            className={Number(item?.id || 0) > 0 ? "selectable" : ""}
          >
            <td className="time" title={Helpers.EST(item.when, "MM/dd/yyyy HH:mm:ss")}>{Helpers.EST(item.when, "MM/dd/yyyy HH:mm:ss")}</td>
            <td className="message">{item.message || "N/A"}</td>
          </tr>
        )}
      />
    );
  }

  function getPendingAdminTaskCount(tasks: { statusId?: number }[] | undefined) {
    if (!Array.isArray(tasks)) return 0;
    return tasks.filter((t) => Number(t?.statusId) === 1).length;
  }

  function renderAdminTasks() {
    const tasks = dashboardData?.adminTasks ?? [];
    return (
      <ClientSortableTable
        columns={[
          { key: "completed", label: "Completed", filterable: false, sortable: true },
          { key: "createTime", label: "Create Time", filterable: false, sortable: true },
          { key: "status", label: "Status", filterable: true, sortable: true },
          { key: "description", label: "Description", filterable: true, sortable: true },
          { key: "id", label: "Id", filterable: false, sortable: true },
        ]}
        data={tasks}
        tableClass="clientSortable clientFilterable expanded adminTasksTable"
        caption={
          <>
            <span onClick={onToggleCollapse} className="tableCaption">
              Pending Tasks
              <span className="captionCount">({tasks.length.toString()})</span>
            </span>
            <span className="captionRight">
              <span className="captionRight"><LuChevronsDown className="collapse" /><LuChevronsUp className="expand" /></span>
            </span>
          </>
        }
        onCaptionClick={onToggleCollapse}
        renderRow={(item: { id: number; completed?: boolean; createTime?: string; status?: string; description?: string }, index: number) => (
          <tr key={item.id ?? index}>
            <td className="center">
              {item.completed ? (
                <MdCheckBox title="Completed" className="checkIcon" />
              ) : (
                <MdCheckBoxOutlineBlank title="Not completed" className="checkIcon" />
              )}
            </td>
            <td title={Helpers.EST(item.createTime, "MM/dd/yyyy HH:mm:ss")}>
              {Helpers.EST(item.createTime, "MM/dd/yyyy HH:mm")}
            </td>
            <td>{item.status}</td>
            <td>{item.description}</td>
            <td>{item.id}</td>
          </tr>
        )}
      />
    );
  }

  function getRecentErrorCountLastHour(rows: DashboardErrorRow[] | undefined) {
    if (!Array.isArray(rows)) return 0;
    const cutoff = Date.now() - 60 * 60 * 1000;
    return rows.filter((row) => {
      const when = row?.when;
      if (!when) return false;
      const timestamp = new Date(when).getTime();
      return !Number.isNaN(timestamp) && timestamp >= cutoff;
    }).length;
  }

  return (
    <div className="adminDashboardTab">
      {adminPerms?.includes(254) && renderSignalRMessages()}
      <div className="dashboardStageCards">
        {[
          { id: "pending",  label: "Assessments Pending User",      data: dashboardData.pendingTests },
          { id: "active",   label: "Assessments In Progress",        data: dashboardData.activeTests },
          { id: "gradable", label: "Assessments Pending Grading",    data: dashboardData.gradableTests },
          { id: "recent",   label: "Assessments Recently Completed", data: dashboardData.recentTests },
          { id: "timedout", label: "Timed Out / Expired Tests",      data: dashboardData.timedOutTests },
          { id: "admintasks", label: "Pending Tasks", data: dashboardData.adminTasks ?? [], count: getPendingAdminTaskCount(dashboardData.adminTasks) },
          ...(adminPerms?.includes(254)
            ? [{ id: "errors", label: "Errors", data: dashboardData.recentErrors }]
            : []),
        ].map((stage) => (
          <button
            key={stage.id}
            type="button"
            className={`dashboardStageCard${selectedStage === stage.id ? " isActive" : ""}`}
            onClick={() => setSelectedStage(stage.id)}
          >
            <div className="stageCardLabel">{stage.label}</div>
            <div className="stageCardCount">{stage.count ?? stage.data?.length ?? 0}</div>
            {stage.id === "errors" && (
              <div className="stageCardRecentCount">
                Last hour: {getRecentErrorCountLastHour(stage.data as DashboardErrorRow[] | undefined)}
              </div>
            )}
          </button>
        ))}
      </div>
      <div className="dashboardStageDetail">
        {selectedStage === "pending"  && renderPendingTests()}
        {selectedStage === "active"   && renderActiveTests()}
        {selectedStage === "gradable" && renderGradableTests()}
        {selectedStage === "recent"   && renderRecentTests()}
        {selectedStage === "timedout" && renderTimedOutTests()}
        {selectedStage === "errors" && adminPerms?.includes(254) && renderRecentErrors()}
        {selectedStage === "admintasks" && renderAdminTasks()}
      </div>
      <TestEditModal isOpen={isEditModalOpen} onClose={onCloseTestEditModalAsync} checkedItems={selectedTests} updateSpinner={updateSpinner} />
      <TestDetailsModal onClose={closeScoreModal} onOpenMonitor={setMonitorTestInstanceId} testInstanceId={testInstanceId} updateSpinner={updateSpinner} isSystemAdmin={adminPerms?.includes(254) || false} initialActiveTab={detailsActiveTab} adminPerms={adminPerms}/>
      <TestInstanceModal isOpen={instanceModalState.testInstanceId != null} onClose={() => setInstanceModalState({ testId: null, testInstanceId: null })} testId={instanceModalState.testId} testInstanceId={instanceModalState.testInstanceId} updateSpinner={updateSpinner} />
      <TestMonitorModal isOpen={monitorTestInstanceId != null} onClose={() => setMonitorTestInstanceId(null)} testInstanceId={monitorTestInstanceId} updateSpinner={updateSpinner} />
      <JobDescriptionModal jobId={jobDescriptionJobId} jobDescriptionId={null} updateSpinner={updateSpinner} onClose={() => setJobDescriptionJobId(null)} />
      <AuditLogModal
        isOpen={selectedAuditLogId !== null}
        logId={selectedAuditLogId}
        onClose={() => setSelectedAuditLogId(null)}
        updateSpinner={updateSpinner}
      />
    </div>
  );
}
